import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@eventbrite/marmalade';
import { useIntl } from 'react-intl';
import styles from './ExpandableBio.module.scss';

const BIO_PREVIEW_MAX_CHARS = 180;
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

interface Props {
  html: string;
}

export function ExpandableBio({ html }: Props) {
  const intl = useIntl();
  const [bioExpanded, setBioExpanded] = useState(false);

  if (!html.trim()) return null;
  const longBioByPlainLength = useMemo(
    () => approximatePlainTextLengthFromHtml(html) > BIO_PREVIEW_MAX_CHARS,
    [html],
  );

  const bioContentRef = useRef<HTMLDivElement>(null);
  const [bioOverflowWhenCollapsed, setBioOverflowWhenCollapsed] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const el = bioContentRef.current;
    if (!el || !html.trim() || bioExpanded || longBioByPlainLength) {
      setBioOverflowWhenCollapsed(false);
      return;
    }

    const measure = () => {
      setBioOverflowWhenCollapsed(el.scrollHeight > el.clientHeight + 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [html, bioExpanded, longBioByPlainLength]);

  const bioCollapsedClamp = !bioExpanded && (longBioByPlainLength || bioOverflowWhenCollapsed);
  const showBioToggle =
    Boolean(html.trim()) && (bioExpanded || longBioByPlainLength || bioOverflowWhenCollapsed);

  return (
    <div className={bioCollapsedClamp ? `${styles.bio} ${styles.bioCollapsed}` : styles.bio}>
      <div
        ref={bioContentRef}
        className={
          bioCollapsedClamp
            ? `${styles.bioContent} ${styles.bioContentCollapsed}`
            : styles.bioContent
        }
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showBioToggle ? (
        <Button
          variant="ghost"
          type="button"
          sizeVariant="small"
          onClick={() => setBioExpanded(!bioExpanded)}
          className={styles.readMore}
        >
          {bioExpanded
            ? intl.formatMessage({ id: 'Show less', defaultMessage: 'Show less' })
            : intl.formatMessage({ id: 'Read more', defaultMessage: 'Read more' })}
        </Button>
      ) : null}
    </div>
  );
}

function approximatePlainTextLengthFromHtml(html: string): number {
  if (!html.trim()) return 0;
  const stripped = html.replace(/<[^>]*>/g, ' ');
  const decoded = stripped
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return decoded.replace(/\s+/g, ' ').trim().length;
}
